/**
 * Plantillas por defecto para contratos de mantenimiento.
 * Texto plano (sin HTML). Saltos de línea con \n.
 */

export interface ClausulaContrato {
  titulo: string;
  contenido: string;
}

export interface PlantillaContrato {
  tipo: 'seguridad' | 'pci';
  nombre: string;
  objeto: string;
  clausulas: ClausulaContrato[];
  texto_legal: string;
}

export const PLANTILLA_SEGURIDAD: PlantillaContrato = {
  tipo: 'seguridad',
  nombre: 'Contrato de mantenimiento de sistemas de seguridad',
  objeto:
    'El presente contrato tiene por objeto la prestación de los servicios de mantenimiento preventivo y correctivo de los sistemas de seguridad (intrusión, CCTV y/o control de accesos) instalados en las dependencias del CLIENTE, conforme al alcance y a las condiciones que se especifican en el presente documento, y de acuerdo con lo dispuesto en la Ley 5/2014 de Seguridad Privada y el Reglamento de Seguridad Privada (RD 2364/1994).',
  clausulas: [
    {
      titulo: '1. Alcance del servicio',
      contenido:
        'El PRESTADOR se compromete a realizar las revisiones reglamentarias de los sistemas relacionados en el apartado "Sistemas incluidos" del presente contrato.\n\nEl mantenimiento incluye la revisión anual obligatoria establecida en la normativa vigente, así como la comprobación del correcto funcionamiento de todos los elementos del sistema (centrales, detectores, sirenas, comunicadores, cámaras, etc.).',
    },
    {
      titulo: '2. Periodicidad de las revisiones',
      contenido:
        'Las revisiones presenciales se realizarán con una periodicidad anual, conforme al Reglamento de Seguridad Privada. Adicionalmente, el PRESTADOR podrá realizar verificaciones remotas trimestrales del estado del sistema cuando éste lo permita.',
    },
    {
      titulo: '3. Asistencias técnicas',
      contenido:
        'Las averías comunicadas por el CLIENTE serán atendidas en horario laboral en un plazo máximo de 48 horas. El precio del presente contrato NO incluye el coste del material averiado ni la mano de obra de las averías ajenas al mantenimiento preventivo, que serán facturadas aparte conforme a las tarifas vigentes del PRESTADOR.',
    },
    {
      titulo: '4. Precio y forma de pago',
      contenido:
        'El precio del servicio es el indicado en el apartado "Condiciones económicas" del presente contrato. El IVA se aplicará conforme a la normativa vigente. El pago se realizará por domiciliación bancaria en la cuenta facilitada por el CLIENTE.',
    },
    {
      titulo: '5. Duración y renovación',
      contenido:
        'El contrato entrará en vigor en la fecha indicada y tendrá la duración especificada en sus condiciones particulares. Salvo denuncia escrita por cualquiera de las partes con una antelación mínima de 30 días a su vencimiento, el contrato se renovará tácitamente por períodos iguales.',
    },
    {
      titulo: '6. Obligaciones del cliente',
      contenido:
        'El CLIENTE se compromete a facilitar el acceso a las instalaciones para la realización de las revisiones, a comunicar cualquier anomalía detectada y a no manipular el sistema sin autorización del PRESTADOR.',
    },
    {
      titulo: '7. Protección de datos',
      contenido:
        'Ambas partes se comprometen a tratar los datos personales conforme al Reglamento (UE) 2016/679 (RGPD) y la LOPDGDD 3/2018. Cuando el servicio implique acceso a imágenes de CCTV, el PRESTADOR actuará como Encargado del Tratamiento.',
    },
  ],
  texto_legal:
    'Empresa de seguridad inscrita en el Registro Nacional de Empresas de Seguridad del Ministerio del Interior. Este contrato se rige por la Ley 5/2014 de Seguridad Privada y normativa de desarrollo.',
};

export const PLANTILLA_PCI: PlantillaContrato = {
  tipo: 'pci',
  nombre: 'Contrato de mantenimiento de instalaciones de protección contra incendios',
  objeto:
    'El presente contrato tiene por objeto la prestación de los servicios de mantenimiento de las instalaciones de protección contra incendios (PCI) ubicadas en las dependencias del CLIENTE, conforme al Reglamento de Instalaciones de Protección contra Incendios (RD 513/2017) y demás normativa aplicable.',
  clausulas: [
    {
      titulo: '1. Alcance del servicio',
      contenido:
        'El PRESTADOR realizará las operaciones de mantenimiento sobre los sistemas relacionados en el apartado "Sistemas incluidos" del presente contrato (detección y alarma, extintores, BIEs, hidrantes, exutorios, puertas cortafuegos, extinción automática, alumbrado de emergencia, etc.) conforme al RD 513/2017.',
    },
    {
      titulo: '2. Periodicidad de las revisiones',
      contenido:
        'Las revisiones se realizarán con la periodicidad TRIMESTRAL y ANUAL establecida en el RD 513/2017 y sus tablas de mantenimiento. El PRESTADOR planificará y ejecutará las revisiones por iniciativa propia, comunicando las fechas al CLIENTE con antelación suficiente.\n\nLa revisión QUINQUENAL (cada 5 años) y el RETIMBRADO de extintores y cilindros NO están incluidos en este contrato salvo pacto expreso en el apartado "Servicios incluidos".',
    },
    {
      titulo: '3. Anomalías y reparaciones',
      contenido:
        'El PRESTADOR documentará todas las anomalías detectadas y emitirá presupuesto previo para su reparación. El precio del contrato NO incluye el coste de reposición de materiales, recargas, retimbres ni reparaciones derivadas de averías o desperfectos.',
    },
    {
      titulo: '4. Asistencias técnicas',
      contenido:
        'Las averías comunicadas por el CLIENTE serán atendidas en horario laboral en un plazo máximo de 48 horas. Los desplazamientos y horas de trabajo en asistencias correctivas se facturarán conforme a las tarifas vigentes del PRESTADOR salvo pacto en contrario.',
    },
    {
      titulo: '5. Precio y forma de pago',
      contenido:
        'El precio del servicio es el indicado en el apartado "Condiciones económicas". El IVA se aplicará conforme a la normativa vigente. El pago se realizará mediante domiciliación bancaria en la cuenta facilitada por el CLIENTE.',
    },
    {
      titulo: '6. Duración y renovación',
      contenido:
        'El contrato entrará en vigor en la fecha indicada y tendrá la duración especificada en sus condiciones particulares. Salvo denuncia escrita por cualquiera de las partes con una antelación mínima de 30 días a su vencimiento, el contrato se renovará tácitamente por períodos iguales.',
    },
    {
      titulo: '7. Obligaciones del cliente',
      contenido:
        'El CLIENTE se compromete a facilitar el acceso a las instalaciones para la realización de las revisiones, a no manipular el sistema sin autorización del PRESTADOR y a custodiar el Libro/Registro de mantenimiento conforme al RD 513/2017.',
    },
    {
      titulo: '8. Protección de datos',
      contenido:
        'Ambas partes se comprometen a tratar los datos personales conforme al Reglamento (UE) 2016/679 (RGPD) y la LOPDGDD 3/2018.',
    },
  ],
  texto_legal:
    'Empresa mantenedora de sistemas de protección contra incendios habilitada conforme al RD 513/2017. Este contrato se rige por el citado Reglamento y demás normativa aplicable en materia de protección contra incendios.',
};

export const CLAUSULA_CRA: ClausulaContrato = {
  titulo: 'Conexión a Central Receptora de Alarmas (CRA)',
  contenido:
    'El mantenimiento del sistema de intrusión incluye la conexión y supervisión a través de la Central Receptora de Alarmas (CRA) autorizada, garantizando la transmisión de eventos de alarma conforme a la normativa vigente en materia de seguridad privada.',
};

export const TITULO_CLAUSULA_CRA = CLAUSULA_CRA.titulo;

export function plantillaPorDefecto(tipo: 'seguridad' | 'pci'): PlantillaContrato {
  return tipo === 'seguridad' ? PLANTILLA_SEGURIDAD : PLANTILLA_PCI;
}

/** Limpia HTML y caracteres de control para que el texto sea seguro en el PDF. */
export function sanitizarTextoContrato(input: string | null | undefined): string {
  if (!input) return '';
  return String(input)
    // Quita etiquetas HTML
    .replace(/<\/?[^>]+(>|$)/g, '')
    // Decodifica entidades comunes
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // Normaliza saltos
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Elimina caracteres de control salvo \n
    .replace(/[\x00-\x09\x0B-\x1F\x7F]/g, '')
    .trim();
}
